import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Grid2, Skeleton, Typography } from "@mui/material";

import Greetings from "./Greetings/Greetings";
import { Breadcrum } from "../MainComponents/Breadcrumb/Breadcrumb";
import { IndicatorCard } from "./Static/IndicatorCard";
import { TrendChartCard } from "./Static/TrendChartCard";
import { AlertStack } from "./Static/AlertStack";
import { useStaticIndicators } from "./Static/hooks/useStaticIndicators";
import { useStaticTrends } from "./Static/hooks/useStaticTrends";
import { useStaticAlerts } from "./Static/hooks/useStaticAlerts";
import { useReportActivity } from "./Static/hooks/useReportActivity";
import { useEthicalScore } from "./Static/hooks/useEthicalScore";
import { ScoreInsights } from "./Static/ScoreInsights";
import { useCommunication } from "./Static/hooks/useCommunication";

// Small titled section wrapper, with an optional right-aligned action.
const Section = ({ title, action, children }) => (
  <Box sx={{ mt: 4 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        flexWrap: "wrap",
        mb: 1.5,
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {action}
    </Box>
    {children}
  </Box>
);

// Inline "prev | current" year switch, e.g. "2025 | 2026".
const YearToggle = ({ years, value, onChange }) => (
  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
    {years.map((y, i) => (
      <Box key={y} sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
        {i > 0 && (
          <Typography component="span" sx={{ color: "text.disabled" }}>
            |
          </Typography>
        )}
        <Typography
          component="button"
          type="button"
          onClick={() => onChange(y)}
          sx={{
            border: "none",
            background: "none",
            cursor: "pointer",
            p: 0,
            fontSize: "0.95rem",
            fontWeight: value === y ? 700 : 500,
            color: value === y ? "primary.main" : "text.secondary",
            borderBottom: value === y ? "2px solid" : "2px solid transparent",
            borderColor: value === y ? "primary.main" : "transparent",
            transition: "color 0.15s ease",
            "&:hover": { color: "primary.main" },
          }}
        >
          {y}
        </Typography>
      </Box>
    ))}
  </Box>
);

/**
 * DashboardStatic — the static "home" dashboard: severe-open alerts, report
 * indicators by status and trend charts, all fed by real data through the EQL
 * hooks (useStaticAlerts / useStaticIndicators / useStaticTrends). Indicators
 * and trends are scoped to the selected year (see the year toggle).
 *
 * The dynamic/customizable dashboards (Dashboardv2) live under Analytics at
 * /analytics/dashboards.
 */
export const DashboardStatic = () => {
  const [t] = useTranslation("dashboard");
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const { indicators, loading: indicatorsLoading } = useStaticIndicators(selectedYear);
  const { trends, loading: trendsLoading } = useStaticTrends(selectedYear);
  const { alerts, loading: alertsLoading } = useStaticAlerts();
  const { activity, loading: activityLoading } = useReportActivity();
  const {
    scoreChart,
    reason: scoreReason,
    loading: scoreLoading,
  } = useEthicalScore(selectedYear);
  const { chart: commChart, loading: commLoading } = useCommunication(selectedYear);

  // El Score Ético va en segunda posición dentro de Tendencias. Cuando no se
  // puede calcular (sin plantilla configurada) se deja una tarjeta con el aviso
  // en su lugar, para no mover de sitio las demás gráficas.
  const trendCards = useMemo(() => {
    const scoreCard = scoreChart || (scoreReason === "no_employees"
      ? {
          id: "ethical-score",
          title: t("static_dashboard.ethical_score"),
          emptyMessage: t("static_dashboard.ethical_score_no_employees"),
        }
      : null);
    const list = [...trends];
    if (scoreCard) list.splice(1, 0, scoreCard);
    // Comunicación cierra la sección: describe la conversación de las
    // denuncias, no su composición.
    if (commChart) list.push(commChart);
    return list;
  }, [trends, scoreChart, scoreReason, commChart, t]);

  return (
    <div className="dashboard">
      {/* Breadcrumb — always at the very top, full width. */}
      {/* <Box sx={{ mb: 2 }}>
        <Breadcrum
          items={[
            { label: t("dashboard.home"), route: "/" },
            { label: t("static_dashboard.title") },
          ]}
        />
      </Box> */}

      {/* Greeting row */}
      {/* <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        <Greetings />
        <Typography variant="body2" color="text.secondary">
          {t("static_dashboard.subtitle")}
        </Typography>
      </Box> */}

      {/* Alerts row — severe reports still open (real data). */}
      {alertsLoading ? (
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 2 }} />
      ) : (
        <AlertStack
          title={t("static_dashboard.severe_open_alerts")}
          iconName="bold_warning_2"
          alerts={alerts}
        />
      )}

      {/* Indicators — report counts by status for the selected year (real data). */}
      <Section
        title={t("static_dashboard.indicators_title")}
        action={
          <YearToggle
            years={[currentYear - 1, currentYear]}
            value={selectedYear}
            onChange={setSelectedYear}
          />
        }
      >
        <Grid2 container spacing={2}>
          {indicatorsLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Grid2 key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                <Skeleton variant="rounded" height={92} sx={{ borderRadius: 2 }} />
              </Grid2>
            ))
          ) : indicators.length === 0 ? (
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t("static_dashboard.empty_indicators")}
              </Typography>
            </Grid2>
          ) : (
            indicators.map((ind) => (
              <Grid2 key={ind.id} size={{ xs: 12, sm: 6, md: 3 }}>
                <IndicatorCard
                  {...ind}
                  onClick={() => {
                    const params = new URLSearchParams({ status: ind.id });
                    // Only past years need scoping; the current year is the list default.
                    if (selectedYear !== currentYear) {
                      params.set("year_created", selectedYear);
                    }
                    navigate(`/reports/all/?${params.toString()}`);
                  }}
                />
              </Grid2>
            ))
          )}
        </Grid2>
      </Section>

      {/* Activity — rolling 7/30/60/90-day intake, each vs. the previous
          window of the same length. Not scoped to the year toggle: these are
          always "the last N days from today". */}
      <Section title={t("static_dashboard.activity_title")}>
        <Grid2 container spacing={2}>
          {activityLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Grid2 key={i} size={{ xs: 12, sm: 6, md: 3 }}>
                  <Skeleton variant="rounded" height={92} sx={{ borderRadius: 2 }} />
                </Grid2>
              ))
            : activity.map((a) => (
                <Grid2 key={a.id} size={{ xs: 12, sm: 6, md: 3 }}>
                  <IndicatorCard
                    label={a.label}
                    value={a.value}
                    delta={a.delta}
                    deltaTitle={t("static_dashboard.activity_vs_previous", { count: a.days })}
                    iconName={a.iconName}
                    accentColor={a.accentColor}
                    onClick={() => {
                      const params = new URLSearchParams({
                        r_start_created: a.from,
                        r_end_created: a.to,
                      });
                      navigate(`/reports/all/?${params.toString()}`);
                    }}
                  />
                </Grid2>
              ))}
        </Grid2>
      </Section>

      {/* Trends — real data for the selected year. */}
      <Section title={t("static_dashboard.trends_title")}>
        <Grid2 container spacing={2}>
          {trendsLoading || scoreLoading || commLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <Grid2 key={i} size={{ xs: 12, md: 6 }}>
                  <Skeleton variant="rounded" height={420} sx={{ borderRadius: 2 }} />
                </Grid2>
              ))
            : trendCards.map((trend) => (
                <Grid2 key={trend.id} size={{ xs: 12, md: 6 }}>
                  <TrendChartCard
                    title={trend.title}
                    type={trend.type}
                    chartData={trend.chartData}
                    colors={trend.colors}
                    emptyMessage={trend.emptyMessage}
                    footer={trend.footer}
                    footerNote={trend.footerNote}
                    headerAction={
                      trend.insights ? <ScoreInsights insights={trend.insights} /> : null
                    }
                  />
                </Grid2>
              ))}
        </Grid2>
      </Section>
    </div>
  );
};

export default DashboardStatic;
